/**
 * Supermicro SYS-420GP-TNAR Server Profile
 *
 * Source: research/server-specs.md
 * - 4U form factor
 * - 8-GPU HGX A100 baseboard (legacy platform)
 * - Dual Intel Xeon Scalable 3rd Gen (up to 40 cores each)
 * - Up to 8TB DDR4
 * - 4x 2200W PSU
 * - Air-cooled
 */

import type { ServerProfile } from '../../types/servers';

/**
 * Supermicro SYS-420GP-TNAR with HGX A100 80GB
 * Note: This is a legacy A100-based platform
 */
export const SUPERMICRO_SYS420GP_A100: ServerProfile = {
  id: 'supermicro-sys420gp-a100',
  name: 'Supermicro SYS-420GP-TNAR (HGX A100)',
  role: 'compute',
  vendor: 'Supermicro',
  model: 'SYS-420GP-TNAR',

  cpu: {
    sockets: 2,
    coresPerSocket: 40,
    model: 'Intel Xeon Platinum 8380 (3rd Gen)',
    tdpPerSocketWatts: 270,
    baseClockGHz: 2.3,
    turboClockGHz: 3.4,
  },

  memory: {
    totalGB: 1024,
    channels: 16,
    type: 'DDR4',
    speedMTs: 3200,
    dimmCount: 32,
    capacityPerDimmGB: 32,
  },

  gpu: {
    platformId: 'hgx-h100-80gb', // Using H100 as proxy since A100 not in current platform list
    gpusPerNode: 8,
    customName: 'HGX A100 80GB SXM4',
  },

  expansion: {
    pcieSlots: [
      // OCP 3.0 support plus additional slots
      { generation: 4, lanes: 16, formFactor: 'OCP' },
      { generation: 4, lanes: 16, formFactor: 'FHFL' },
      { generation: 4, lanes: 16, formFactor: 'FHFL' },
      { generation: 4, lanes: 16, formFactor: 'FHFL' },
    ],
    m2Slots: 2,
    drive25Count: 10, // 6 front + 4 rear NVMe
    drive35Count: 0,
  },

  network: {
    onboard1GbE: 0,
    onboard10GbE: 0,
    onboard25GbE: 0,
    bmcPorts: 1,
  },

  power: {
    maxPowerWatts: 8800,
    typicalPowerWatts: 5000,
    idlePowerWatts: 400,
    redundancy: 'N+1',
    psuCount: 4,
    psuCapacityWatts: 2200,
    efficiency: '80 PLUS Titanium',
    inputVoltage: '200-240V AC',
  },

  dimensions: {
    rackUnits: 4,
    widthMm: 482.6,
    depthMm: 900,
    heightMm: 178,
    weightKg: 70,
    maxAltitudeMeters: 3000,
  },

  environmental: {
    tempRange: { min: 10, max: 35 },
    humidityRange: { min: 8, max: 90 },
    maxBtuPerHour: 17060, // 5000W * 3.412
    coolingType: 'air',
    noiseLevelDbA: 75,
  },

  metadata: {
    productUrl: 'https://www.supermicro.com/en/products/system/gpu/4u/sys-420gp-tnar',
    notes: 'Legacy HGX A100 platform - consider SYS-821GE-TNHR or SYS-421GE-TNHR2-LCC for H100/H200',
    validated: true,
  },
} as const;
